import { apiClient, type ApiEnvelope } from './client';
import type { Paginated } from './agentApi';

// ── Real-time voice agents (Gemini Live) ────────────────────────────────────
export type VoiceDirection = 'inbound' | 'outbound' | 'both';
export type VoiceStatus = 'active' | 'inactive' | 'draft';

export interface VoiceCustomTool {
  name: string;
  description?: string | null;
  url: string;
  schema?: Record<string, unknown> | null;
  headers?: Record<string, string> | null;
  body_template?: Record<string, unknown> | null;
}

export interface VoiceAgentRow {
  id: number;
  name: string;
  slug?: string | null;
  tenant_id?: number | null;
  status: VoiceStatus;
  direction: VoiceDirection;
  system_prompt?: string | null;
  greeting_text?: string | null;
  voice_name?: string | null;
  gemini_model?: string | null;
  language?: string | null;
  max_call_seconds?: number | null;
  memory_enabled?: boolean;
  enabled_tools?: string[] | null;
  custom_tools?: VoiceCustomTool[] | null;
  assignments_count?: number;
  calls_count?: number;
  assignments?: VoiceAssignment[];
  settings?: Record<string, unknown> | null;
  // מנוע קול (נשמר ב-settings; נשלח שטוח בשמירה): gemini | elevenlabs.
  voice_provider?: 'gemini' | 'elevenlabs';
  // סגנון דיבור (settings, נשלח שטוח): לשון פנייה + מילון הגייה.
  address_style?: 'neutral' | 'male' | 'female';
  pronunciation_map?: Record<string, string> | null;
  elevenlabs_voice_id?: string | null;
  elevenlabs_model_id?: string | null;
  // יכולות Eleven v3 (נשמרות ב-settings): מצב יציבות, דיבור אנושי, תגיות אודיו.
  elevenlabs_stability?: string;
  elevenlabs_humanize?: boolean;
  elevenlabs_tags?: string[];
  // פרמטרים מתקדמים — מודל השיחה (top-level).
  thinking_level?: string | null;
  temperature?: number | null;
  // קצב שיחה (VAD) + קול ElevenLabs — נשמרים ב-settings, נשלחים שטוח בשמירה.
  vad_silence_ms?: number;
  vad_end_sensitivity?: string | null;
  vad_start_sensitivity?: string | null;
  vad_prefix_ms?: number;
  elevenlabs_similarity?: number;
  elevenlabs_speaker_boost?: boolean;
  // כלי ספרייה מקושרים: המזהים נשלחים בשמירה, הפירוט מוחזר בקריאת סוכן בודד.
  tool_ids?: number[];
  library_tools?: VoiceLibraryTool[];
  // זרימת שיחה מקושרת (null = ללא זרימה).
  flow_id?: number | null;
  /** מתי הקונפיג נדחף לאחרונה לספק מתארח (ElevenLabs Agents). */
  last_deployed_at?: string | null;
  /** 'local' = השיחה מנוהלת אצלנו; 'elevenlabs' = הסוכן מתארח אצלם. */
  agent_host?: 'local' | 'elevenlabs';
  elevenlabs_agent_id?: string | null;
  // ── Voice v3 (settings keys, נשלחים שטוח בשמירה) ──
  knowledge_base_id?: number | null;
  webhook?: { enabled?: boolean; include?: string[] } | null;
  qa_scoring?: boolean;
  hours?: { mode?: string; custom?: { day: number; enabled?: boolean; open?: string; close?: string }[] } | null;
  after_hours?: { message?: string | null } | null;
  transfer_targets?: { key: string; label?: string; type: 'extension' | 'external'; target: string; hours_gated?: boolean; when?: Record<string, unknown> | null }[] | null;
  noise_profile?: 'normal' | 'noisy' | 'off';
  payment?: {
    method?: 'none' | 'link' | 'dtmf';
    provider?: 'green_invoice' | 'generic' | 'cardcom';
    send_trigger_token?: string | null;
    description_template?: string | null;
    max_amount?: number | null;
    link_tool?: { url?: string; headers?: Record<string, string>; body_template?: Record<string, unknown>; url_path?: string } | null;
  } | null;
}

export interface VoicePaymentCredential {
  configured: boolean;
  provider?: string;
  account_name?: string | null;
  is_active?: boolean;
  has_key?: boolean;
  has_secret?: boolean;
  last_verified_at?: string | null;
  callback_url?: string;
}

export interface VoiceAssignment {
  id?: number;
  voice_agent_id?: number;
  tenant_id: number;
  direction_override?: VoiceDirection | null;
}

/** סוכן שמתארח ב-ElevenLabs Agents. */
export interface ElevenLabsHostedAgent {
  agent_id: string;
  name: string;
  /** מסומן כשהסוכן כבר משויך לסוכן מרכזיה אחר — למניעת שיוך כפול. */
  linked_to?: { id: number; name: string } | null;
}

/** קול של OpenAI Realtime, מתוך הקטלוג בשרת. */
export interface OpenAiVoice {
  id: string;
  label: string;
  gender: 'male' | 'female' | 'neutral';
  /** מומלץ ע"י OpenAI לאיכות — לא דירוג עברית מדוד. */
  recommended: boolean;
  note?: string;
}

export interface OpenAiCatalog {
  voices: OpenAiVoice[];
  limits: Record<string, { min: number; max: number; step: number; default?: number }>;
  /** שדות שאינם רלוונטיים למנוע הזה — ה-UI מסתיר אותם. */
  unsupported: string[];
  configured: boolean;
}

/** מודל שיחה מותר, מתוך ה-allow-list של השרת. */
export interface VoiceLiveModel {
  id: string;
  label: string;
  newest?: boolean;
  is_default?: boolean;
  /** מנוע השיחה שהמודל רץ עליו. */
  engine?: 'gemini' | 'openai';
  /** false = למנוע אין מפתח API מוגדר, ולכן אי אפשר לבחור בו. */
  available?: boolean;
}

export interface VoiceToolCatalogItem {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

export interface VoiceSession {
  id: number;
  voice_agent_id: number;
  tenant_id?: number | null;
  direction?: string | null;
  status?: string | null;
  caller_number?: string | null;
  destination_number?: string | null;
  transcript?: string | null;
  summary?: string | null;
  disposition?: string | null;
  billed_seconds?: number | null;
  cost_estimate?: number | null;
  tools_used?: unknown[] | null;
  recording_play_url?: string | null;
  ai_metrics?: {
    score?: number | null;
    flow_adherence?: number | null;
    politeness?: number | null;
    correctness?: number | null;
    resolution?: string | null;
    notes?: string[] | null;
  } | null;
  started_at?: string | null;
  ended_at?: string | null;
  created_at?: string | null;
}

export interface VoiceAgentStats {
  days: number;
  total_calls: number;
  by_status: Record<string, number>;
  per_day: Record<string, number>;
  avg_duration_sec: number;
  avg_score: number | null;
  scored_calls: number;
  resolutions: Record<string, number>;
  funnel: {
    completed: number;
    stages: { stage_key: string; name?: string | null; entered: number; dropped_here: number }[];
  } | null;
}

export interface VoiceKbDocument {
  id: number;
  title: string;
  type: string;
  status: string;
  chunk_count?: number;
  content?: string;
  url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface VoiceKbView {
  knowledge_base: { id: number; name: string; business_id: number };
  documents: VoiceKbDocument[];
}

export interface VoiceRecordColumn {
  id: number;
  voice_agent_id: number;
  key: string;
  label: string;
  type: string;
  options?: { value: string; label?: string }[] | null;
  required?: boolean;
  order?: number;
  default_value?: string | null;
}

export interface VoiceRecordRow {
  id: number;
  voice_agent_id: number;
  tenant_id?: number | null;
  voice_agent_call_id?: number | null;
  data: Record<string, unknown>;
  created_by?: string | null;
  created_at?: string | null;
}

export interface VoiceMemory {
  id: number;
  voice_agent_id: number;
  tenant_id?: number | null;
  phone_number: string;
  customer_name?: string | null;
  summary?: string | null;
  facts?: Array<{ fact?: string; source?: string; at?: string }> | null;
  preferred_address_style?: 'male' | 'female' | null;
  call_count?: number;
  last_call_at?: string | null;
}

// ── ספריית כלי API קוליים (Voice Tools) ─────────────────────────────────────
export type VoiceToolMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type VoiceToolParamIn = 'query' | 'body';
export type VoiceToolParamSource = 'static' | 'variable' | 'from_model';
export type VoiceToolParamType = 'string' | 'number' | 'integer' | 'boolean';

export interface VoiceToolParam {
  name: string;
  in: VoiceToolParamIn;
  source: VoiceToolParamSource;
  value?: string | null;
  type?: VoiceToolParamType;
  description?: string | null;
  required?: boolean;
  enum?: string[] | null;
}

export interface VoiceToolResponseMapping {
  path: string;
  variable: string;
}

export interface VoiceTool {
  id: number;
  tenant_id?: number | null;
  name: string;
  description?: string | null;
  method: VoiceToolMethod;
  url: string;
  headers?: Record<string, string> | null;
  params?: VoiceToolParam[] | null;
  timeout_ms?: number | null;
  speak_while_waiting?: string | null;
  on_error_instruction?: string | null;
  response_mappings?: VoiceToolResponseMapping[] | null;
  return_mapped_only?: boolean;
  agents_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface VoiceToolTestResult {
  ok: boolean;
  request: {
    method?: string;
    url?: string;
    headers?: Record<string, string>;
    query?: Record<string, unknown>;
    body?: Record<string, unknown> | null;
  };
  status: number | null;
  duration_ms: number | null;
  response: unknown;
  mapped_variables?: Record<string, unknown> | null;
  error?: string | null;
}

export interface VoiceSecret {
  id: number;
  tenant_id?: number | null;
  key: string;
  created_at?: string | null;
}

/** כלי ספרייה מקושר לסוכן קול (מוחזר מ-GET /voice-agents/{id}). */
export interface VoiceLibraryTool {
  id: number;
  name: string;
  description?: string | null;
}

// ── זרימות קול (Flow Builder) ────────────────────────────────────────────────
export type VoiceStageType = 'start' | 'normal' | 'function' | 'transfer' | 'end_call';

export interface VoiceStageVariable {
  name: string;
  type: string;
  description?: string | null;
  required?: boolean;
}

export interface VoiceFlowStage {
  key: string;
  name: string;
  type: VoiceStageType;
  prompt?: string | null;
  allowed_tools?: string[];
  collect_variables?: VoiceStageVariable[];
  auto_tool_id?: number | null;
  transfer_target?: string | null;
  canvas?: { x: number; y: number } | null;
}

export type VoiceGuardOp = 'eq' | 'ne' | 'contains' | 'not_contains' | 'exists' | 'not_exists' | 'gt' | 'lt' | 'in';

export interface VoiceTransitionGuard {
  variable: string;
  op: VoiceGuardOp;
  value?: string | string[];
}

export interface VoiceFlowTransition {
  from: string;
  to: string;
  condition_description?: string | null;
  guard?: VoiceTransitionGuard | null;
}

export interface VoiceFlowRow {
  id: number;
  tenant_id?: number | null;
  name: string;
  description?: string | null;
  stages_count?: number;
  agents_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface VoiceFlowGraph {
  flow: { id: number; name: string; description?: string | null };
  stages: VoiceFlowStage[];
  transitions: VoiceFlowTransition[];
}

export interface VoiceFlowValidation {
  errors: string[];
  warnings: string[];
}

// ── תצוגה מקדימה / סימולציה / סטטיסטיקה של זרימה ─────────────────────────────
export interface VoiceFlowStagePreview {
  stage_key: string;
  block: string;
  internal_tools: string[];
  note?: string | null;
}

export type VoiceSimEvent =
  | { type: 'move_to_stage'; stage_key?: string; reason?: string }
  | { type: 'move_rejected'; stage_key?: string; reason?: string }
  | { type: 'save_variable'; name?: string; value?: unknown }
  | { type: 'tool_stub'; tool?: string }
  | { type: string; [k: string]: unknown };

export interface VoiceSimState {
  stage_key?: string | null;
  variables?: Record<string, unknown>;
}

export interface VoiceSimTurn {
  reply: string;
  state: { stage_key: string | null; variables: Record<string, unknown> };
  events: VoiceSimEvent[];
}

export interface VoiceFlowStats {
  days: number;
  total_calls: number;
  completed: number;
  stages: Array<{ stage_key: string; entered: number; dropped_here: number }>;
}

// ── שיוך מספרים + שיחת נסיון + live (Phase D) ─────────────────────────────────
export interface VoiceAgentNumber {
  id: number;
  number: string;
  tenant_id?: number | null;
  route_type?: string | null;
  assigned_to_this: boolean;
  assigned_to_other_agent: boolean;
}

export interface VoiceAgentExtension {
  id: number;
  ext: string;
  name?: string | null;
  type?: string | null;
  status?: string | null;
}

// ── קטלוג ElevenLabs (מודלים, קולות, ותגיות/מצבי-יציבות של Eleven v3) ─────────
export interface ElevenLabsV3TagGroup {
  key: string;
  label: string;
  tags: string[];
}
export interface ElevenLabsCatalog {
  models: { id: string; name: string; hebrew: boolean }[];
  voices: { id: string; name: string }[];
  v3_tags?: ElevenLabsV3TagGroup[];
  v3_stability_modes?: string[];
}

export interface VoiceLiveCall {
  id: number;
  direction?: string | null;
  caller_number?: string | null;
  status?: string | null;
  variables?: Record<string, unknown> | null;
  current_stage_id?: string | null;
  stage_history?: Array<{ stage?: string | null; key?: string | null; reason?: string | null; at?: string | null }> | null;
  started_at?: string | null;
}

export interface CatalogModel {
  id: string;
  name: string;
  description: string;
  context_length: number;
  price_in_per_m: number;
  price_out_per_m: number;
  audio_input: boolean;
  transcribable: boolean;
}

export interface EngineRow {
  id: number;
  model_id: string;
  label?: string | null;
  status: 'candidate' | 'approved' | 'disabled';
  bench?: {
    sample_count: number; avg_overlap_pct: number; keyword_agreement_pct?: number | null;
    avg_latency_ms: number; passed: boolean;
  } | null;
  benched_at?: string | null;
}

export const superApi = {
  async dashboard() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/super/dashboard');
    return data.data;
  },
  async health() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/super/health');
    return data.data;
  },

  async tenants(params?: Record<string, unknown>): Promise<Paginated<unknown>> {
    const { data } = await apiClient.get('/super/tenants', { params });
    return data as Paginated<unknown>;
  },
  async tenant(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/super/tenants/${id}`);
    return data.data;
  },
  async createTenant(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/super/tenants', payload);
    return data.data;
  },
  async updateTenant(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.put<ApiEnvelope<unknown>>(`/super/tenants/${id}`, patch);
    return data.data;
  },
  async suspendTenant(id: number) {
    await apiClient.delete(`/super/tenants/${id}`);
  },
  /** Permanently erase a tenant and all its data (tickets preserved). Requires the slug as confirmation. */
  async deleteTenant(id: number, confirm: string) {
    const { data } = await apiClient.delete<ApiEnvelope<{ recording_files: number; tickets_preserved: number }>>(`/super/tenants/${id}/force`, { data: { confirm } });
    return data;
  },
  async activateTenant(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/super/tenants/${id}/activate`);
    return data.data;
  },
  async tenantUsers(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>(`/super/tenants/${id}/users`);
    return data.data ?? [];
  },
  async createTenantUser(id: number, payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/super/tenants/${id}/users`, payload);
    return data.data;
  },
  async updateTenantUser(id: number, userId: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/super/tenants/${id}/users/${userId}`, patch);
    return data.data;
  },
  async resetTenantUserPassword(id: number, userId: number, password: string) {
    await apiClient.post(`/super/tenants/${id}/users/${userId}/reset-password`, { password });
  },
  async resetTenantUser2fa(id: number, userId: number) {
    await apiClient.post(`/super/tenants/${id}/users/${userId}/reset-2fa`);
  },
  async suspendTenantUser(id: number, userId: number) {
    await apiClient.delete(`/super/tenants/${id}/users/${userId}`);
  },

  /** Permanently delete a tenant user (not just suspend). */
  async deleteTenantUser(id: number, userId: number) {
    await apiClient.delete(`/super/tenants/${id}/users/${userId}/force`);
  },
  async tenantBilling(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/super/tenants/${id}/billing`);
    return data.data;
  },
  async impersonateTenant(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/super/tenants/${id}/impersonate`);
    return data.data;
  },

  async plans() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/super/plans');
    return data.data ?? [];
  },
  async createPlan(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/super/plans', payload);
    return data.data;
  },
  async updatePlan(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/super/plans/${id}`, patch);
    return data.data;
  },
  async deletePlan(id: number) {
    await apiClient.delete(`/super/plans/${id}`);
  },

  /** Per-tenant charge overview for a month (users+minutes priced). */
  async billingOverview(month?: string) {
    const { data } = await apiClient.get('/super/billing/overview', { params: { month } });
    return data.data;
  },

  // Platform cost/margin analysis: our cost per tenant vs what we bill them.
  async costs(month?: string): Promise<CostsOverview> {
    const { data } = await apiClient.get<ApiEnvelope<CostsOverview>>('/super/costs', { params: { month } });
    return data.data!;
  },
  async updateCostRate(key: string, patch: { rate_agorot?: number; label?: string; enabled?: boolean }): Promise<CostRate> {
    const { data } = await apiClient.put<ApiEnvelope<CostRate>>(`/super/costs/rates/${key}`, patch);
    return data.data!;
  },
  async createCostRate(payload: { key: string; label: string; unit: string; rate_agorot: number; meta?: Record<string, unknown> }): Promise<CostRate> {
    const { data } = await apiClient.post<ApiEnvelope<CostRate>>('/super/costs/rates', payload);
    return data.data!;
  },

  /** סטטוס המפתח הכללי (OpenRouter) — בלי לחשוף אותו. */
  async platformKeys(): Promise<{ openrouter: PlatformOpenRouterKey }> {
    const { data } = await apiClient.get<ApiEnvelope<{ openrouter: PlatformOpenRouterKey }>>('/super/platform/keys');
    return data.data!;
  },
  /** שמירת המפתח הכללי; ערך ריק/null מוחק (חזרה ל-env אם קיים). */
  async updatePlatformOpenRouterKey(apiKey: string | null): Promise<PlatformOpenRouterKey> {
    const { data } = await apiClient.put<ApiEnvelope<PlatformOpenRouterKey>>('/super/platform/keys/openrouter', { api_key: apiKey });
    return data.data!;
  },

  async invoices(params?: Record<string, unknown>): Promise<Paginated<unknown>> {
    const { data } = await apiClient.get('/super/invoices', { params });
    return data as Paginated<unknown>;
  },
  async markInvoicePaid(id: number) {
    await apiClient.post(`/super/invoices/${id}/paid`);
  },

  async trunks() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/super/trunks');
    return data.data ?? [];
  },
  async createTrunk(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/super/trunks', payload);
    return data.data;
  },
  async updateTrunk(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/super/trunks/${id}`, patch);
    return data.data;
  },
  async deleteTrunk(id: number) {
    await apiClient.delete(`/super/trunks/${id}`);
  },
  async testTrunk(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ reachable: boolean; online: boolean; endpoint_state: string; status: string; latency_ms: number; error: string | null }>>(`/super/trunks/${id}/test`);
    return data.data;
  },
  async trunksStatus(): Promise<{ id: number; endpoint_state: string; active_channels: number }[]> {
    const { data } = await apiClient.get<ApiEnvelope<{ id: number; endpoint_state: string; active_channels: number }[]>>('/super/trunks/status');
    return data.data ?? [];
  },

  async didPool(params?: Record<string, unknown>): Promise<Paginated<unknown>> {
    const { data } = await apiClient.get('/super/did-pool', { params });
    return data as Paginated<unknown>;
  },
  // ── מנועי תמלול (ניהול allow-list + בדיקת איכות) ─────────────────
  async engines(kind?: string): Promise<{ engines: EngineRow[]; env_models: string[]; min_overlap_pct: number }> {
    const { data } = await apiClient.get('/super/engines', { params: kind ? { kind } : undefined });
    return data.data;
  },
  async enginesCatalog(kind: string, q?: string, fresh?: boolean): Promise<CatalogModel[]> {
    const { data } = await apiClient.get('/super/engines/catalog', { params: { kind, q: q || undefined, fresh: fresh ? 1 : undefined } });
    return (data.data as CatalogModel[]) ?? [];
  },
  async addEngine(modelId: string, label?: string, kind?: string): Promise<EngineRow> {
    const { data } = await apiClient.post('/super/engines', { model_id: modelId, label: label || null, kind: kind || undefined });
    return data.data;
  },
  async benchEngine(id: number, opts?: { tenant_id?: number; keywords?: string; limit?: number }): Promise<EngineRow> {
    // הבדיקה מתמללת כמה הקלטות אמיתיות דרך OpenRouter+Gemini — עלול לחרוג
    // מ-30s ברירת המחדל של axios, לכן timeout מורחב.
    const { data } = await apiClient.post(`/super/engines/${id}/bench`, opts ?? {}, { timeout: 180_000 });
    return data.data;
  },
  async updateEngine(id: number, patch: { status?: string; label?: string | null; force?: boolean }): Promise<EngineRow> {
    const { data } = await apiClient.patch(`/super/engines/${id}`, patch);
    return data.data;
  },
  async deleteEngine(id: number): Promise<void> {
    await apiClient.delete(`/super/engines/${id}`);
  },

  async updateDidPricing(id: number, patch: { monthly_price_cents: number; purchase_price_cents: number }) {
    const { data } = await apiClient.patch(`/super/did-pool/${id}/pricing`, patch);
    return data.data;
  },
  async addDids(payload: { number?: string; numbers?: string[]; type?: string; trunk_id?: number | null; monthly_price_cents?: number; purchase_price_cents?: number }) {
    const { data } = await apiClient.post<ApiEnvelope<{ added: number; skipped: number }>>('/super/did-pool', payload);
    return data.data!;
  },
  async deleteDid(id: number) {
    await apiClient.delete(`/super/did-pool/${id}`);
  },
  async assignDid(id: number, tenantId: number, extra?: { route_type?: string | null; route_target?: string | null; trunk_id?: number | null }) {
    await apiClient.post(`/super/did-pool/${id}/assign`, { tenant_id: tenantId, ...extra });
  },
  async unassignDid(id: number) {
    await apiClient.post(`/super/did-pool/${id}/unassign`);
  },

  async audit(params?: Record<string, unknown>): Promise<Paginated<unknown>> {
    const { data } = await apiClient.get('/super/audit', { params });
    return data as Paginated<unknown>;
  },

  async reports(): Promise<Array<{ id: string; he: string; description: string }>> {
    const { data } = await apiClient.get<ApiEnvelope<Array<{ id: string; he: string; description: string }>>>('/super/reports');
    return data.data ?? [];
  },
  async runReport<T = unknown>(id: string, params?: Record<string, unknown>): Promise<T> {
    const { data } = await apiClient.get<ApiEnvelope<T>>(`/super/reports/${id}`, { params });
    return data.data as T;
  },

  async notifications() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/super/notifications');
    return { items: (data.data ?? []) as unknown[], meta: data.meta };
  },

  async tickets(status?: string) {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/super/tickets', { params: { status } });
    return data.data ?? [];
  },

  async ticket(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/super/tickets/${id}`);
    return data.data;
  },

  async replyTicket(id: number, body: string, status?: string) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/super/tickets/${id}/reply`, { body, status });
    return data.data;
  },

  // Internal (platform) users
  async internalUsers() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/super/internal-users');
    return data.data ?? [];
  },
  async createInternalUser(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/super/internal-users', payload);
    return data.data;
  },
  async updateInternalUser(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/super/internal-users/${id}`, patch);
    return data.data;
  },
  async suspendInternalUser(id: number) {
    await apiClient.delete(`/super/internal-users/${id}`);
  },
  async resetInternalUser2fa(id: number) {
    await apiClient.post(`/super/internal-users/${id}/reset-2fa`);
  },

  // Platform-level API & Webhooks settings
  async platformApi() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/super/api');
    return data.data;
  },
  async platformApiKeys() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/super/api/keys');
    return data.data ?? [];
  },
  async createApiKey(payload: { tenant_id: number; name: string; scopes?: string[] | null; expires_at?: string | null }) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number; key: string; prefix: string }>>('/super/api/keys', payload);
    return data.data!;
  },
  async platformWebhooks() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/super/api/webhooks');
    return data.data ?? [];
  },
  // לוגים מערכתיים: כל בקשות ה-API הציבורי + כל משלוחי ה-Webhooks.
  async apiRequestLogs(params?: Record<string, unknown>) {
    const { data } = await apiClient.get('/super/api/request-logs', { params });
    return data as { data: unknown[]; meta: { current_page: number; last_page: number; total: number } };
  },
  /** בקשה אחת במלואה + אבחון בעברית (לחיצה על שורה ב-/super/api). */
  async apiRequestLog(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<ApiRequestLogDetail>>(`/super/api/request-logs/${id}`);
    return data.data!;
  },
  async apiWebhookLogs(params?: Record<string, unknown>) {
    const { data } = await apiClient.get('/super/api/webhook-logs', { params });
    return data as { data: unknown[]; meta: { current_page: number; last_page: number; total: number } };
  },
  async createWebhook(payload: { tenant_id: number; url: string; events: string[] }) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number; signing_secret: string }>>('/super/api/webhooks', payload);
    return data.data!;
  },
  async deleteWebhook(id: number) {
    await apiClient.delete(`/super/api/webhooks/${id}`);
  },
  async revokeApiKey(id: number) {
    await apiClient.delete(`/super/api/keys/${id}`);
  },
  async testWebhook(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ ok: boolean; status: number | null; latency_ms: number | null; error: string | null }>>(`/super/api/webhooks/${id}/test`);
    return data.data;
  },
  async toggleWebhook(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number; active: boolean }>>(`/super/api/webhooks/${id}/toggle`);
    return data.data;
  },

  // Real-time voice agents (Gemini Live). Sub-routes live under /super/voice-agents.
  async voiceAgents(params?: Record<string, unknown>): Promise<Paginated<VoiceAgentRow>> {
    const { data } = await apiClient.get('/super/voice-agents', { params });
    return data as Paginated<VoiceAgentRow>;
  },
  async voiceAgent(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<VoiceAgentRow>>(`/super/voice-agents/${id}`);
    return data.data!;
  },
  async voiceTools() {
    const { data } = await apiClient.get<ApiEnvelope<VoiceToolCatalogItem[]>>('/super/voice-agents/tools');
    return data.data ?? [];
  },
  /**
   * מודלי השיחה המותרים (allow-list מאומת בשרת). `engine` מבדיל בין
   * Gemini Live ל-OpenAI Realtime; `available=false` = המנוע בלי מפתח API.
   */
  async voiceModels(): Promise<{ models: VoiceLiveModel[]; default: string }> {
    const { data } = await apiClient.get<ApiEnvelope<{ models: VoiceLiveModel[]; default: string }>>('/super/voice-agents/models');
    return data.data ?? { models: [], default: '' };
  },
  /** סוכני ElevenLabs Agents בחשבון — לבורר ולאימוץ סוכן שנוצר בקונסולה שלהם. */
  async elevenlabsAgents(): Promise<{ agents: ElevenLabsHostedAgent[]; configured: boolean }> {
    const { data } = await apiClient.get<ApiEnvelope<{ agents: ElevenLabsHostedAgent[]; configured: boolean }>>('/super/voice-agents/elevenlabs-agents');
    return data.data ?? { agents: [], configured: false };
  },
  /** דחיפת הקונפיג המקומי (פרומפט מלא כולל ניקוד, קול, LLM) ל-ElevenLabs. */
  async elevenlabsDeploy(id: number): Promise<{ elevenlabs_agent_id: string; last_deployed_at: string; created: boolean }> {
    const { data } = await apiClient.post<ApiEnvelope<{ elevenlabs_agent_id: string; last_deployed_at: string; created: boolean }>>(`/super/voice-agents/${id}/elevenlabs/deploy`);
    return data.data!;
  },
  /** משיכת שיחות שהתקיימו אצל ElevenLabs לטבלה שלנו. */
  async elevenlabsSync(id: number): Promise<{ imported: number }> {
    const { data } = await apiClient.post<ApiEnvelope<{ imported: number }>>(`/super/voice-agents/${id}/elevenlabs/sync`);
    return data.data ?? { imported: 0 };
  },
  /** קטלוג OpenAI Realtime: קולות, גבולות פרמטרים, ושדות לא-נתמכים. */
  async openaiCatalog(): Promise<OpenAiCatalog> {
    const { data } = await apiClient.get<ApiEnvelope<OpenAiCatalog>>('/super/voice-agents/openai-catalog');
    return data.data ?? { voices: [], limits: {}, unsupported: [], configured: false };
  },
  /**
   * דגימת קול (MP3) כ-blob URL.
   *
   * חובה למשוך דרך apiClient ולא לתת ל-<audio> כתובת ישירה: האימות הוא
   * Bearer בכותרת, ו-`new Audio(url)` לא מצרף כותרות — הבקשה הגיעה בלי
   * טוקן והדגימה נכשלה. הקורא אחראי ל-URL.revokeObjectURL.
   */
  async openaiVoicePreview(voice: string): Promise<string> {
    const { data } = await apiClient.get<Blob>('/super/voice-agents/openai-voice-preview', {
      params: { voice },
      responseType: 'blob',
    });
    return URL.createObjectURL(data);
  },
  /** קטלוג מודלי + קולות ElevenLabs לבורר מנוע הקול (+ תגיות/מצבי-יציבות של v3). */
  async elevenLabsCatalog(): Promise<ElevenLabsCatalog> {
    const { data } = await apiClient.get<ApiEnvelope<ElevenLabsCatalog>>('/super/voice-agents/elevenlabs-catalog');
    return data.data ?? { models: [], voices: [] };
  },
  async createVoiceAgent(payload: Partial<VoiceAgentRow>) {
    const { data } = await apiClient.post<ApiEnvelope<VoiceAgentRow>>('/super/voice-agents', payload);
    return data.data!;
  },
  async updateVoiceAgent(id: number, patch: Partial<VoiceAgentRow>) {
    const { data } = await apiClient.patch<ApiEnvelope<VoiceAgentRow>>(`/super/voice-agents/${id}`, patch);
    return data.data!;
  },
  async deleteVoiceAgent(id: number) {
    await apiClient.delete(`/super/voice-agents/${id}`);
  },
  async syncVoiceAssignments(id: number, assignments: VoiceAssignment[]) {
    const { data } = await apiClient.put<ApiEnvelope<VoiceAssignment[]>>(`/super/voice-agents/${id}/assignments`, { assignments });
    return data.data ?? [];
  },
  async voiceSessions(id: number, params?: Record<string, unknown>): Promise<Paginated<VoiceSession>> {
    const { data } = await apiClient.get(`/super/voice-agents/${id}/sessions`, { params });
    return data as Paginated<VoiceSession>;
  },
  async voiceMemories(id: number, params?: Record<string, unknown>): Promise<Paginated<VoiceMemory>> {
    const { data } = await apiClient.get(`/super/voice-agents/${id}/memories`, { params });
    return data as Paginated<VoiceMemory>;
  },
  async deleteVoiceMemory(id: number, memoryId: number) {
    await apiClient.delete(`/super/voice-agents/${id}/memories/${memoryId}`);
  },
  /** Voice v3: סטטיסטיקות פר-סוכן (נפח/משך/QA/משפך). */
  async voiceAgentStats(id: number, days = 30): Promise<VoiceAgentStats> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceAgentStats>>(`/super/voice-agents/${id}/stats`, { params: { days } });
    return data.data!;
  },
  /** Voice v3: טבלת הרשומות הדינמית של הסוכן. */
  async voiceRecordColumns(id: number): Promise<VoiceRecordColumn[]> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceRecordColumn[]>>(`/super/voice-agents/${id}/records/columns`);
    return data.data ?? [];
  },
  async createVoiceRecordColumn(id: number, column: Partial<VoiceRecordColumn>): Promise<VoiceRecordColumn> {
    const { data } = await apiClient.post<ApiEnvelope<VoiceRecordColumn>>(`/super/voice-agents/${id}/records/columns`, column);
    return data.data!;
  },
  async updateVoiceRecordColumn(id: number, columnId: number, patch: Partial<VoiceRecordColumn>): Promise<VoiceRecordColumn> {
    const { data } = await apiClient.patch<ApiEnvelope<VoiceRecordColumn>>(`/super/voice-agents/${id}/records/columns/${columnId}`, patch);
    return data.data!;
  },
  async deleteVoiceRecordColumn(id: number, columnId: number) {
    await apiClient.delete(`/super/voice-agents/${id}/records/columns/${columnId}`);
  },
  async voiceRecordRows(id: number, params?: Record<string, unknown>): Promise<Paginated<VoiceRecordRow>> {
    const { data } = await apiClient.get(`/super/voice-agents/${id}/records/rows`, { params });
    return data as Paginated<VoiceRecordRow>;
  },
  async updateVoiceRecordRow(id: number, rowId: number, rowData: Record<string, unknown>): Promise<VoiceRecordRow> {
    const { data } = await apiClient.patch<ApiEnvelope<VoiceRecordRow>>(`/super/voice-agents/${id}/records/rows/${rowId}`, { data: rowData });
    return data.data!;
  },
  async deleteVoiceRecordRow(id: number, rowId: number) {
    await apiClient.delete(`/super/voice-agents/${id}/records/rows/${rowId}`);
  },
  /** Voice v3: מאגרי ידע (RAG) לבורר בטופס הסוכן. */
  async voiceKnowledgeBases(): Promise<{ id: number; name: string; business_id: number; business_name?: string | null }[]> {
    const { data } = await apiClient.get<ApiEnvelope<{ id: number; name: string; business_id: number; business_name?: string | null }[]>>('/super/voice-agents/knowledge-bases');
    return data.data ?? [];
  },
  async voiceKb(id: number): Promise<VoiceKbView> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceKbView>>(`/super/voice-agents/${id}/knowledge`);
    return data.data!;
  },
  async voiceKbDocument(id: number, docId: number): Promise<VoiceKbDocument> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceKbDocument>>(`/super/voice-agents/${id}/knowledge/documents/${docId}`);
    return data.data!;
  },
  async addVoiceKbDocument(id: number, body: { type: 'text' | 'url'; title: string; content?: string; url?: string }): Promise<VoiceKbDocument> {
    const { data } = await apiClient.post<ApiEnvelope<VoiceKbDocument>>(`/super/voice-agents/${id}/knowledge/documents`, body);
    return data.data!;
  },
  async updateVoiceKbDocument(id: number, docId: number, body: { title?: string; content?: string; url?: string }): Promise<VoiceKbDocument> {
    const { data } = await apiClient.patch<ApiEnvelope<VoiceKbDocument>>(`/super/voice-agents/${id}/knowledge/documents/${docId}`, body);
    return data.data!;
  },
  async deleteVoiceKbDocument(id: number, docId: number) {
    await apiClient.delete(`/super/voice-agents/${id}/knowledge/documents/${docId}`);
  },
  async reprocessVoiceKbDocument(id: number, docId: number): Promise<VoiceKbDocument> {
    const { data } = await apiClient.post<ApiEnvelope<VoiceKbDocument>>(`/super/voice-agents/${id}/knowledge/documents/${docId}/reprocess`, {});
    return data.data!;
  },
  async voiceKbSearch(id: number, q: string): Promise<unknown> {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/super/voice-agents/${id}/knowledge/search`, { params: { q } });
    return data.data;
  },
  async voicePaymentCredential(id: number): Promise<VoicePaymentCredential> {
    const { data } = await apiClient.get<ApiEnvelope<VoicePaymentCredential>>(`/super/voice-agents/${id}/payment-credential`);
    return data.data!;
  },
  async saveVoicePaymentCredential(id: number, body: { provider: string; api_key?: string; api_secret?: string; extra?: Record<string, unknown>; account_name?: string; is_active?: boolean }): Promise<VoicePaymentCredential> {
    const { data } = await apiClient.put<ApiEnvelope<VoicePaymentCredential>>(`/super/voice-agents/${id}/payment-credential`, body);
    return data.data!;
  },
  async deleteVoicePaymentCredential(id: number) {
    await apiClient.delete(`/super/voice-agents/${id}/payment-credential`);
  },
  /** Place an outbound voice-agent call to a phone number. */
  async dialVoiceAgent(id: number, to: string, tenantId?: number | null) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/super/voice-agents/${id}/dial`, {
      to,
      ...(tenantId != null ? { tenant_id: tenantId } : {}),
    });
    return data;
  },

  // ── ספריית כלי API קוליים ───────────────────────────────────────
  async voiceToolsLibrary(params?: Record<string, unknown>): Promise<Paginated<VoiceTool>> {
    const { data } = await apiClient.get('/super/voice-tools', { params });
    return data as Paginated<VoiceTool>;
  },
  async voiceTool(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<VoiceTool>>(`/super/voice-tools/${id}`);
    return data.data!;
  },
  async createVoiceTool(payload: Partial<VoiceTool>) {
    const { data } = await apiClient.post<ApiEnvelope<VoiceTool>>('/super/voice-tools', payload);
    return data.data!;
  },
  async updateVoiceTool(id: number, patch: Partial<VoiceTool>) {
    const { data } = await apiClient.patch<ApiEnvelope<VoiceTool>>(`/super/voice-tools/${id}`, patch);
    return data.data!;
  },
  async deleteVoiceTool(id: number) {
    await apiClient.delete(`/super/voice-tools/${id}`);
  },
  async testVoiceTool(id: number, payload: { sample_variables?: Record<string, unknown>; model_arguments?: Record<string, unknown> }) {
    // בדיקה מבצעת קריאת HTTP אמיתית ל-API החיצוני — עלול לחרוג מ-30s של axios.
    const { data } = await apiClient.post<ApiEnvelope<VoiceToolTestResult>>(`/super/voice-tools/${id}/test`, payload, { timeout: 60_000 });
    return data.data!;
  },

  // ── סודות קוליים (ערכים נשמרים מוצפנים ולא מוחזרים לעולם) ────────
  async voiceSecrets(params?: { tenant_id?: number | null }): Promise<VoiceSecret[]> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceSecret[]>>('/super/voice-secrets', { params });
    return data.data ?? [];
  },
  async saveVoiceSecret(payload: { tenant_id?: number | null; key: string; value: string }) {
    const { data } = await apiClient.put<ApiEnvelope<VoiceSecret>>('/super/voice-secrets', payload);
    return data.data!;
  },
  async deleteVoiceSecret(id: number) {
    await apiClient.delete(`/super/voice-secrets/${id}`);
  },

  // ── זרימות קול (Flow Builder) ───────────────────────────────────
  async voiceFlows(params?: Record<string, unknown>): Promise<Paginated<VoiceFlowRow>> {
    const { data } = await apiClient.get('/super/voice-flows', { params });
    return data as Paginated<VoiceFlowRow>;
  },
  async createVoiceFlow(payload: { tenant_id?: number | null; name: string; description?: string | null }) {
    const { data } = await apiClient.post<ApiEnvelope<VoiceFlowRow>>('/super/voice-flows', payload);
    return data.data!;
  },
  async updateVoiceFlow(id: number, patch: { name?: string; description?: string | null; tenant_id?: number | null }) {
    const { data } = await apiClient.patch<ApiEnvelope<VoiceFlowRow>>(`/super/voice-flows/${id}`, patch);
    return data.data!;
  },
  async deleteVoiceFlow(id: number) {
    await apiClient.delete(`/super/voice-flows/${id}`);
  },
  async voiceFlowGraph(id: number): Promise<VoiceFlowGraph> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceFlowGraph>>(`/super/voice-flows/${id}/graph`);
    return data.data!;
  },
  /** שמירה אטומית של הגרף. זורק שגיאה (422) עם errors/warnings ב-response.data. */
  async saveVoiceFlowGraph(id: number, body: { stages: VoiceFlowStage[]; transitions: VoiceFlowTransition[] }): Promise<{ warnings: string[] }> {
    const { data } = await apiClient.put<ApiEnvelope<{ warnings?: string[] }>>(`/super/voice-flows/${id}/graph`, body);
    return { warnings: data.data?.warnings ?? (data as { warnings?: string[] }).warnings ?? [] };
  },
  async validateVoiceFlow(id: number, body: { stages: VoiceFlowStage[]; transitions: VoiceFlowTransition[] }): Promise<VoiceFlowValidation> {
    const { data } = await apiClient.post<ApiEnvelope<VoiceFlowValidation> & VoiceFlowValidation>(`/super/voice-flows/${id}/validate`, body);
    return { errors: data.errors ?? data.data?.errors ?? [], warnings: data.warnings ?? data.data?.warnings ?? [] };
  },
  /** מה המודל רואה בשלב מסוים (הבלוק שנשלח לפרומט + כלים פנימיים). */
  async voiceFlowStagePreview(id: number, stageKey: string): Promise<VoiceFlowStagePreview> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceFlowStagePreview>>(`/super/voice-flows/${id}/preview/${encodeURIComponent(stageKey)}`);
    return data.data!;
  },
  /** סימולציית טקסט של תור בשיחה מול הזרימה השמורה. 422 אם אין שלבים; throttle 30/דק'. */
  async simulateVoiceFlow(id: number, body: {
    message: string;
    agent_id?: number | null;
    state?: VoiceSimState;
    history?: Array<{ role: 'user' | 'model'; text: string }>;
  }): Promise<VoiceSimTurn> {
    const { data } = await apiClient.post<ApiEnvelope<VoiceSimTurn>>(`/super/voice-flows/${id}/simulate`, body);
    return data.data!;
  },
  async voiceFlowStats(id: number, days = 30): Promise<VoiceFlowStats> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceFlowStats>>(`/super/voice-flows/${id}/stats`, { params: { days } });
    return data.data!;
  },

  // ── שיוך מספרים בקליק + שיחת נסיון + live (Phase D) ──────────────
  async voiceAgentNumbers(id: number): Promise<VoiceAgentNumber[]> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceAgentNumber[]>>(`/super/voice-agents/${id}/numbers`);
    return data.data ?? [];
  },
  /** שיוך אטומי של DIDs לסוכן. 422 עם message ב-response.data על קונפליקט. */
  async setVoiceAgentNumbers(id: number, didIds: number[]): Promise<VoiceAgentNumber[]> {
    const { data } = await apiClient.put<ApiEnvelope<VoiceAgentNumber[]>>(`/super/voice-agents/${id}/numbers`, { did_ids: didIds });
    return data.data ?? [];
  },
  /** שלוחות ה-tenant של הסוכן, לבורר שיחת הנסיון. */
  async voiceAgentExtensions(id: number): Promise<VoiceAgentExtension[]> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceAgentExtension[]>>(`/super/voice-agents/${id}/extensions`);
    return data.data ?? [];
  },
  /** שיחת נסיון — הסופטפון של השלוחה בדפדפן מצלצל. 422 עם message על כשל. */
  async voiceAgentTestCall(id: number, extensionId: number, tenantId?: number | null): Promise<{ success: boolean; message?: string }> {
    const { data } = await apiClient.post<ApiEnvelope<unknown> & { success: boolean; message?: string }>(
      `/super/voice-agents/${id}/test-call`,
      { extension_id: extensionId, ...(tenantId != null ? { tenant_id: tenantId } : {}) },
    );
    return { success: data.success, message: data.message };
  },
  async voiceAgentLive(id: number): Promise<VoiceLiveCall[]> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceLiveCall[]>>(`/super/voice-agents/${id}/live`);
    return data.data ?? [];
  },

  // ── טוקני MCP ל-Claude Code ────────────────────────────────────────────
  async mcpTokens(): Promise<McpTokenRow[]> {
    const { data } = await apiClient.get<ApiEnvelope<{ tokens: McpTokenRow[] }>>('/super/mcp-tokens');
    return data.data?.tokens ?? [];
  },
  async createMcpToken(payload: { name?: string; read_only?: boolean; expires_in_days?: number }): Promise<McpTokenCreated> {
    const { data } = await apiClient.post<ApiEnvelope<McpTokenCreated>>('/super/mcp-tokens', payload);
    return data.data as McpTokenCreated;
  },
  async revokeMcpToken(tokenId: number) {
    await apiClient.delete(`/super/mcp-tokens/${tokenId}`);
  },
};

export interface McpTokenRow {
  id: number;
  name: string;
  read_only: boolean;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
}

/** תשובת ההנפקה — ה-token מוצג פעם אחת בלבד. */
export interface McpTokenCreated {
  token: string;
  token_id: number;
  name: string;
  read_only: boolean;
  expires_at: string;
  mcp_url: string;
  claude_code_command: string;
  mcp_json: Record<string, unknown>;
  /** תצורה ל-Claude Desktop (דרך גשר mcp-remote). אופציונלי — שרת ישן לא מחזיר. */
  claude_desktop_config?: Record<string, unknown>;
  claude_desktop_config_path?: { macos: string; windows: string };
}

// ── Platform cost analysis ─────────────────────────────────────────
export interface CostRate {
  key: string;
  label: string;
  unit: string;
  rate_agorot: number;
  enabled: boolean;
  meta?: Record<string, unknown> | null;
  updated_at?: string | null;
}
export interface CostLine {
  key: string;
  label: string;
  unit: string;
  quantity: number;
  rate_agorot: number;
  cost_agorot: number;
}
/** מקור המפתח שממנו המרכזיה תתמלל: אישי / כללי-מערכתי / אין. */
export type OpenRouterKeySource = 'personal' | 'system' | 'none';
export interface CostRow {
  tenant_id: number;
  tenant_name: string;
  tenant_slug: string;
  status: string;
  month: string;
  lines: CostLine[];
  cost_agorot: number;
  revenue_agorot: number;
  margin_agorot: number;
  pricing_configured: boolean;
  openrouter_key_source: OpenRouterKeySource;
  /** עלות AI אמיתית מהספק (USD) — לצד ההערכה מבוססת-התעריף. */
  actual_ai_cost_usd: number;
  actual_transcription_usd: number;
  actual_summary_usd: number;
}
export interface CostsOverview {
  month: string;
  rates: CostRate[];
  rows: CostRow[];
  totals: { cost_agorot: number; revenue_agorot: number; margin_agorot: number; actual_ai_cost_usd: number };
  openrouter_system_key: { configured: boolean };
}

/** סטטוס המפתח הכללי — בלי לחשוף אותו. */
export interface PlatformOpenRouterKey {
  configured: boolean;
  source: 'managed' | 'env' | 'none';
  masked: string | null;
  updated_at?: string | null;
}

/** פירוט בקשת API אחת מהלוג — כולל איך הגיעה ואבחון. */
export interface ApiRequestLogDetail {
  id: number;
  tenant_id: number | null;
  tenant_name: string | null;
  api_key_id: number | null;
  key_name: string | null;
  key_prefix: string | null;
  key_scopes: string[] | null;
  method: string;
  path: string;
  status: number;
  ip: string | null;
  duration_ms: number | null;
  error: string | null;
  created_at: string;
  headers: Record<string, string> | null;
  query: Record<string, unknown> | null;
  body: unknown | null;
  response_body: unknown | null;
  diagnosis: { ok: boolean; title: string; lines: string[] };
}
